import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { DELIVERY_OPTIONS } from '../utils/formatDelivery'; 
import '../styles/AdminNotPageShowForm.css';
import ButtonRegular from './ui/ButtonRegular';
import ButtonDelete from './ui/ButtonDelete';

const AdminNotPageShowForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const deliveryMap = {
        inpost: 'Kurier InPost',
        inpost_cod: 'Kurier InPost za pobraniem',
    };

    // Funkcja dodająca nagłówek z tokenem autoryzacji
    const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

    useEffect(() => {
        const fetchForm = async () => {
            try {
                // Dodano authHeader, aby pobieranie formularza było autoryzowane
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, { headers: authHeader() });
                setForm(res.data);
            } catch (err) {
                setError('Nie udało się pobrać danych formularza.');
            } finally {
                setLoading(false);
            }
        };
        fetchForm();
    }, [id]);

    if (loading) return <p>Ładowanie...</p>;
    if (error) return <p className="error-text">{error}</p>;
    if (!form) return <p>Formularz nie istnieje.</p>;

    const { products = [], customerData = {}, shippingData = {}, deliveryMethod } = form;
    const currentDelivery = DELIVERY_OPTIONS.find(opt => opt.value === deliveryMethod);
    const totalPrice = products.reduce((sum, p) => sum + (p.price || 0), 0);
    const deliveryCost = parseFloat(currentDelivery?.price || 0);
    const grandTotal = totalPrice + deliveryCost;

    // Sprawdzenie, czy dane wysyłkowe różnią się od danych klienta
    const showShippingData =
        shippingData &&
        (shippingData.street !== customerData.street ||
            shippingData.city !== customerData.city ||
            shippingData.postalCode !== customerData.postalCode);

    const deleteForm = async () => {
        if (!window.confirm('Czy na pewno chcesz usunąć ten formularz?')) return;
        setSaving(true);
        try {
            // Użycie authHeader w żądaniu usunięcia (to rozwiązuje wcześniejszy błąd!)
            await axios.delete(`${process.env.REACT_APP_API_URL}/api/forms/${id}`, { headers: authHeader() });
            navigate('/admin/forms');
        } catch (err) {
            console.error(err);
            setError('Nie udało się usunąć formularza');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="form-container">
            <div className="form-content">
                <h1 className="form-title">Podgląd formularza</h1>
                <h3 className="form-subtitle">Podgląd zamówienia nr: {form.formNumber ? String(form.formNumber).padStart(4, '0') : ''}</h3>

                <section className="form-section">
                    <h2>Produkty</h2>
                    {products.length === 0 ? (
                        <p>Brak produktów.</p>
                    ) : (
                        <table className="products-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Nazwa</th>
                                    <th style={{ textAlign: 'right' }}>Cena (PLN)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {products.map((p, i) => (
                                    <tr key={i} className={i % 2 === 0 ? 'table-row-even' : 'table-row-odd'}>
                                        <td>{i + 1}</td>
                                        <td>{p.name}</td>
                                        <td style={{ textAlign: 'right' }}>{p.price ? Number(p.price).toFixed(2) : '0.00'}</td>
                                    </tr>
                                ))}
                                <tr className="table-total-row">
                                    <td colSpan={2}>Razem za produkty</td>
                                    <td style={{ textAlign: 'right' }}>{totalPrice.toFixed(2)}</td>
                                </tr>
                                <tr className="table-total-row">
                                    <td colSpan={2}>Koszt dostawy</td>
                                    <td style={{ textAlign: 'right' }}>{deliveryCost.toFixed(2)}</td>
                                </tr>
                                <tr className="table-total-row">
                                    <td colSpan={2}>Łącznie do zapłaty</td>
                                    <td style={{ textAlign: 'right' }}>{grandTotal.toFixed(2)}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </section>

                <section className="form-section">
                    <h2>Dane klienta</h2>
                    <table className="products-table">
                        <tbody>
                            <tr><td>Imię</td><td>{customerData.firstName}</td></tr>
                            <tr><td>Nazwisko</td><td>{customerData.lastName}</td></tr>
                            <tr><td>Email</td><td>{customerData.email}</td></tr>
                            <tr><td>Telefon</td><td>{customerData.phone}</td></tr>
                            <tr><td>Ulica</td><td>{customerData.street}</td></tr>
                            <tr><td>Numer budynku</td><td>{customerData.building}</td></tr>
                            <tr><td>Numer lokalu</td><td>{customerData.apartment}</td></tr>
                            <tr><td>Miasto</td><td>{customerData.city}</td></tr>
                            <tr><td>Kod pocztowy</td><td>{customerData.postalCode}</td></tr>
                            <tr><td>Dostawa</td><td>{deliveryMap[deliveryMethod] || deliveryMethod}</td></tr>
                            <tr><td>Adres dostawy taki sam jak dane zamawiającego</td><td>{showShippingData ? 'Nie' : 'Tak'}</td></tr>
                            <tr><td>Typ dowodu księgowego</td><td>{customerData.vatInvoice ? 'Faktura VAT' : 'Paragon'}</td></tr>
                            <tr><td>Uwagi</td><td>{customerData.additionalInfo}</td></tr>
                        </tbody>
                    </table>
                </section>

                {showShippingData && (
                    <section className="form-section red-bg">
                        <h2>Dane wysyłkowe</h2>
                        <table className="products-table">
                            <tbody>
                                <tr><td>Ulica</td><td>{shippingData.street}</td></tr>
                                <tr><td>Numer budynku</td><td>{shippingData.building}</td></tr>
                                <tr><td>Numer lokalu</td><td>{shippingData.apartment}</td></tr>
                                <tr><td>Miasto</td><td>{shippingData.city}</td></tr>
                                <tr><td>Kod pocztowy</td><td>{shippingData.postalCode}</td></tr>
                            </tbody>
                        </table>
                    </section>
                )}

                {customerData.vatInvoice && (
                    <section className="form-section">
                        <h2>Dane do faktury VAT</h2>
                        <table className="products-table">
                            <tbody>
                                <tr><td>Nazwa firmy</td><td>{customerData.companyName}</td></tr>
                                <tr><td>Adres firmy</td><td>{customerData.companyAddress}</td></tr>
                                <tr><td>NIP</td><td>{customerData.taxId}</td></tr>
                            </tbody>
                        </table>
                    </section>
                )}

                <div className="form-buttons">
                    <div className="action-buttons" style={{ marginTop: 8 }}>
                        <ButtonDelete onClick={deleteForm} disabled={saving}>
                            {saving ? 'Usuwanie...' : 'Usuń cały formularz'}
                        </ButtonDelete>
                        <ButtonRegular onClick={() => navigate('/admin/forms')} disabled={saving}>
                            ⬅️ Powrót
                        </ButtonRegular>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminNotPageShowForm;